/**
 * @fileoverview `<PaymentSummary>` — résumé de panier portail.
 *
 * Affiche le nombre de liquidations sélectionnées, le total XOF et les limites
 * contractuelles de paiement mobile money en Côte d'Ivoire.
 */

import type { ReactNode } from 'react';
import { Amount } from './amount.tsx';
import { WarningNotice } from './notice.tsx';
import { cn } from '../lib/utils.ts';

export interface PaymentSummaryProps {
  selectedCount: number;
  totalAmount: number;
  minAmount?: number;
  maxAmount?: number;
  action?: ReactNode;
  className?: string;
}

/**
 * Résumé de sélection avant paiement.
 *
 * @param props - Nombre d'items, total, bornes, action et classe CSS.
 * @returns Bloc synthétique avec alertes de bornes si nécessaire.
 */
export function PaymentSummary({
  selectedCount,
  totalAmount,
  minAmount = 300,
  maxAmount = 2_000_000,
  action,
  className,
}: PaymentSummaryProps): React.JSX.Element {
  const aboveMax = totalAmount > maxAmount;
  const belowMin = selectedCount > 0 && totalAmount < minAmount;
  const invalid = aboveMax || belowMin;

  return (
    <section className={cn('border-border bg-card rounded-lg border p-4', className)}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-muted-foreground text-sm">Sélection</p>
          <p className="text-quipux-navy-950 mt-1 text-sm font-medium">
            {selectedCount} liquidation{selectedCount > 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right">
          <p className="text-muted-foreground text-sm">Total à payer</p>
          <Amount value={totalAmount} className="mt-1 block text-xl" />
        </div>
      </div>

      {invalid && (
        <WarningNotice
          className="mt-4"
          title={aboveMax ? 'Plafond dépassé' : 'Montant minimum non atteint'}
        >
          {aboveMax
            ? 'Le total dépasse 2 000 000 F CFA. Sélectionnez moins d’amendes et réglez le reste en une seconde fois.'
            : 'Le montant minimum de paiement est de 300 F CFA.'}
        </WarningNotice>
      )}

      {action && <div className="mt-4">{action}</div>}
    </section>
  );
}
